import {untilDestroyed, UntilDestroy} from "@ngneat/until-destroy";
import {Component, Inject, OnDestroy, OnInit} from "@angular/core";
import {WorkbenchViewComponent} from "../../../shared/components/viewcontainers/workbench-view.component";
import {faChevronDown, faChevronUp} from "@fortawesome/pro-light-svg-icons";
import {WORKBENCH_VIEW_CONFIG, WorkbenchViewConfig} from '../../../shared/types';
import {faExclamationCircle, faQuestionCircle} from "@fortawesome/pro-solid-svg-icons";
import {WorkbenchLayoutManager} from "../../../shared/services/workbench-layout-manager.service";
import {WorkbenchKeyValStore} from '../../../shared/services/workbench-store.service';
import {WorkbenchContextService} from '../../../shared/services/workbench-context.service';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {of} from 'rxjs';
import {distinctUntilChanged, map, switchMap, take} from 'rxjs/operators';

export type ImpairedVisionSimulationName =
    'myopia' | 'glare' | 'yellowing'
    | 'double'
    | 'contrast' | 'protanopia' | 'deuteranopia'
    | 'tritanopia' | 'achromatopsia';

export interface ImpairedVisionSimulationConfig {
    label: string;
    enabled: boolean;
    config?: {
        degree: number;
    }
}

@UntilDestroy()
@Component({
    templateUrl: './impaired-vision-simulation-control-view.component.html',
    styleUrls: ['./impaired-vision-simulation-control-view.component.less']
})
export class ImpairedVisionSimulationControlViewComponent extends WorkbenchViewComponent implements OnInit {

    /**
     * The simulation is rendered as a WebGL overlay on top of the console's canvas. Headless consoles
     * stream into a video element instead, so the overlay would only paint a black rectangle there
     * (WMKNBN-12319). Until the simulation supports video streams, it is unavailable on those consoles.
     */
    static readonly UNAVAILABLE_MESSAGE = 'Impaired vision simulation is not supported by this device console.';

    openedSimulations: {[key: string]: boolean } = {};

    simulationUnavailable = false;

    simulationDescriptions: {[id: string]: string} = {
        'myopia': 'Cataracts are cloudy formations that develop in the lens of the eye, leading to blurred vision. Myopia, also known as nearsightedness, is a condition where distant objects appear blurry, but close objects are seen clearly.',
    };

    simulationConfigs: Record<ImpairedVisionSimulationName, ImpairedVisionSimulationConfig> = {
        'myopia': { label: 'Cataract & Myopia', enabled: false, config: { degree: 1.0 } },
        'glare': { label: 'Glare', enabled: false, config: { degree: 1.0 } },
        'yellowing': {  label: 'Yellowing', enabled: false },
        'double': {  label: 'Double vision', enabled: false },
        'contrast': { label: 'Impaired contrast perception', enabled: false, config: { degree: 0.6 }},
        'protanopia': { label: 'Protanopia', enabled: false },
        'deuteranopia': { label: 'Deuteranopia', enabled: false },
        'tritanopia': { label: 'Tritanopia', enabled: false },
        'achromatopsia': { label: 'Achromatopsia', enabled: false },
    };

    simulations: ImpairedVisionSimulationName[] = [
        'myopia', 'glare', 'yellowing',
        'double', 'contrast', 'protanopia', 'deuteranopia', 'tritanopia', 'achromatopsia'
    ];

    constructor(@Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<any>,
                public store: WorkbenchKeyValStore, public layoutManager: WorkbenchLayoutManager,
                private workbenchContextService: WorkbenchContextService,
                private deviceService: DeviceService) {
        super(layoutManager, config);
    }

    ngOnInit(): void {
        this.workbenchContextService.subscribeWorkbenchContext$().pipe(
            map(context => context.deviceContext?.activeDevice),
            distinctUntilChanged(),
            switchMap(deviceId => deviceId ? this.deviceService.getDevice$(deviceId).pipe(take(1)) : of(undefined)),
            untilDestroyed(this)
        ).subscribe(device => this.simulationUnavailable = !!device?.isHeadlessDevice());
    }

    toggleSimulation(simulation: any) {
        this.openedSimulations[simulation.id] = !this.openedSimulations[simulation.id];
    }

    adjustConsoleManipulations() {
        this.store.patchValue('console.impaired-vision.simulation', this.simulationConfigs);
    }

    protected readonly faChevronDown = faChevronDown;
    protected readonly faChevronUp = faChevronUp;
    protected readonly faQuestionCircle = faQuestionCircle;
    protected readonly faExclamationCircle = faExclamationCircle;
    protected readonly unavailableMessage = ImpairedVisionSimulationControlViewComponent.UNAVAILABLE_MESSAGE;
}
